import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/Button.js';
import { Textarea } from '../ui/Textarea.js';
import { Pill } from '../PillGroup.js';
import { useToast } from '../ui/Toast.js';
import { useCreateDmDraft, useSendDmDraft, useCancelDmDraft, type DmReplyChannel } from '../../hooks/useDmReply.js';

// Responder un DM — vive al pie del bloque de conversación de SU canal (ver
// ContactOutreachTab), nunca flotando aparte: no hay selector de canal acá,
// el canal lo determina el bloque donde este composer está montado. Ciclo
// propone/apruebo/manda: "Enviar" crea un borrador (dm_messages,
// status='draft_pending_approval'), después hay que confirmar para disparar
// el envío real.

interface Props {
  contactId: number;
  channel: DmReplyChannel;
}

export function DmReplyComposer({ contactId, channel }: Props) {
  const { t } = useTranslation();
  const toast = useToast();

  const [body, setBody] = useState('');
  const [pendingDraftId, setPendingDraftId] = useState<number | null>(null);
  const [pendingText, setPendingText] = useState('');
  const [sendError, setSendError] = useState<string | null>(null);

  const createDraft = useCreateDmDraft(contactId);
  const sendDraft = useSendDmDraft(contactId);
  const cancelDraft = useCancelDmDraft(contactId);

  const disabled = !channel.eligible || channel.threadId == null;

  async function handleCreateDraft() {
    if (!channel.threadId || !body.trim()) return;
    try {
      const res = await createDraft.mutateAsync({ channelId: channel.channelId, threadId: channel.threadId, body });
      setPendingDraftId(res.id);
      setPendingText(body);
      setBody('');
      setSendError(null);
    } catch (err) {
      toast.error(t('contacts.dmReply.draftError', { error: err instanceof Error ? err.message : String(err) }));
    }
  }

  async function handleConfirmSend() {
    if (pendingDraftId == null) return;
    const result = await sendDraft.mutateAsync(pendingDraftId);
    if (!result.ok) {
      setSendError(result.error ?? 'Unknown error');
      return;
    }
    setPendingDraftId(null);
    setPendingText('');
    setSendError(null);
    toast.info(t('contacts.dmReply.sentOk'));
  }

  async function handleCancelDraft() {
    if (pendingDraftId == null) return;
    await cancelDraft.mutateAsync(pendingDraftId);
    setPendingDraftId(null);
    setPendingText('');
    setSendError(null);
  }

  if (pendingDraftId != null) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
        <p style={{ fontSize: 'var(--evo-list-subtitle-fs)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)' }}>
          {t('contacts.dmReply.pendingNote')}
        </p>
        <p style={{ fontSize: 'var(--evo-list-title-fs)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)', whiteSpace: 'pre-wrap' }}>
          {pendingText}
        </p>
        {sendError && (
          <p style={{ fontSize: 'var(--evo-list-subtitle-fs)', color: 'var(--evo-danger)', fontFamily: 'var(--evo-font)' }}>
            {t('contacts.dmReply.sendError', { error: sendError })}
          </p>
        )}
        <div style={{ display: 'flex', gap: 'var(--evo-space-1)' }}>
          <Button variant="solid" tone="cyan" size="sm" loading={sendDraft.isPending} onClick={() => void handleConfirmSend()}>
            {t('contacts.dmReply.confirmSend')}
          </Button>
          <Button variant="outline" tone="muted" size="sm" disabled={sendDraft.isPending} onClick={() => void handleCancelDraft()}>
            {t('contacts.dmReply.cancel')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
      {!channel.eligible && channel.reason && (
        <Pill tone="danger" size="xs">{t(`contacts.dmReply.reason.${channel.reason}`)}</Pill>
      )}
      {channel.eligible && channel.closeToExpiring && channel.hoursRemaining != null && (
        <Pill tone="warn" size="xs">
          {t('contacts.dmReply.closeToExpiring', { hours: Math.max(0, Math.floor(channel.hoursRemaining)) })}
        </Pill>
      )}

      <Textarea
        fullWidth
        rows={2}
        placeholder={t('contacts.dmReply.placeholder')}
        value={body}
        onChange={v => setBody(v)}
        disabled={disabled}
      />
      <div>
        <Button
          variant="solid"
          tone="cyan"
          size="sm"
          disabled={disabled || !body.trim()}
          loading={createDraft.isPending}
          onClick={() => void handleCreateDraft()}
        >
          {t('contacts.dmReply.send')}
        </Button>
      </div>
    </div>
  );
}
